import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PortalKioskPage() {
  const { auraKey } = useParams();
  const [aura, setAura] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [exitPin, setExitPin] = useState('');
  const [showExit, setShowExit] = useState(false);
  const [exitError, setExitError] = useState('');
  const fileInput = useRef(null);

  useEffect(() => {
    const fetchAura = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/portal/${auraKey}`);
        setAura(r.data);
      } catch (e) { /* ignore */ }
    };
    fetchAura();
    document.documentElement.requestFullscreen?.().catch(() => {});
  }, [auraKey]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('guest_name', 'Kiosk Guest');
      await axios.post(`${API_URL}/api/media/upload?aura_key=${auraKey}`, fd, { withCredentials: true });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); if (fileInput.current) fileInput.current.value = ''; }, 2500);
    } catch (e) {
      alert('Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const tryExit = async () => {
    setExitError('');
    if (!aura?.id) { setExitError('Cannot verify'); return; }
    try {
      await axios.post(`${API_URL}/api/auras/${aura.id}/kiosk-pin/verify`, { pin: exitPin });
      try { await document.exitFullscreen(); } catch (_) {}
      window.location.href = `/portal/${auraKey}`;
    } catch (e) {
      setExitError('Wrong PIN');
      setExitPin('');
    }
  };

  return (
    <div className="min-h-screen bg-midnight flex flex-col items-center justify-center text-text-light p-6 relative">
      <div className="absolute top-4 right-4">
        <button aria-label="Exit kiosk" onClick={() => setShowExit(true)}
                className="text-xs text-white/30 hover:text-white/60 px-3 py-1 rounded">
          exit
        </button>
      </div>
      <h1 className="font-serif text-4xl md:text-6xl text-champagne mb-2">{aura?.title || 'Welcome'}</h1>
      <p className="text-text-muted text-lg mb-12">Tap below to share a photo</p>

      {success ? (
        <div className="flex flex-col items-center gap-4">
          <div className="w-32 h-32 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Check className="w-16 h-16 text-emerald-400" />
          </div>
          <div className="text-2xl">Thank you!</div>
        </div>
      ) : (
        <button
          aria-label="Take a photo"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="bg-champagne text-midnight rounded-2xl px-12 py-10 text-2xl font-semibold flex items-center gap-4 shadow-2xl hover:scale-105 transition disabled:opacity-50"
        >
          <Camera className="w-12 h-12" />
          {uploading ? 'Uploading…' : 'Tap to take a photo'}
        </button>
      )}
      <input ref={fileInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} />

      {showExit && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-midnight border border-white/20 rounded-xl p-8 w-80 max-w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-serif text-champagne mb-4">Exit kiosk</h2>
            <input
              type="password" inputMode="numeric" pattern="[0-9]*" maxLength={4}
              value={exitPin} onChange={(e) => setExitPin(e.target.value.replace(/\D/g, ''))}
              placeholder="4-digit PIN"
              className="w-full bg-white/5 border border-white/10 rounded px-4 py-3 text-center text-2xl tracking-widest"
              aria-label="Exit PIN"
            />
            {exitError && <div className="text-red-400 text-sm mt-2">{exitError}</div>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowExit(false)} className="flex-1 border border-white/20 rounded px-4 py-2">Cancel</button>
              <button onClick={tryExit} className="flex-1 bg-champagne text-midnight rounded px-4 py-2 font-medium">Exit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
